import { useCallback, useMemo, useState } from "preact/hooks";
import {
  createFormState,
  FormState,
  FREITEXT_MAX,
  isSubmitEnabled,
} from "../state/formState";
import { clampFreitext } from "../utils/validation";

export interface UseFormState {
  state: FormState;
  setRating: (id: string, value: number) => void;
  setFreitext: (value: string) => void;
  setReviewerField: <K extends keyof FormState["reviewer"]>(
    key: K,
    value: FormState["reviewer"][K]
  ) => void;
  setConsent: (value: boolean) => void;
  setDisplayConsent: (value: boolean) => void;
  setAnonymous: (value: boolean) => void;
  submitEnabled: boolean;
}

export function useFormState(initial?: Partial<FormState>): UseFormState {
  const [state, setState] = useState<FormState>(() => {
    const base = createFormState();
    if (!initial) return base;
    const merged = {
      ...base,
      ...initial,
      ratings: { ...base.ratings, ...(initial.ratings ?? {}) },
      reviewer: { ...base.reviewer, ...(initial.reviewer ?? {}) },
    };
    return {
      ...merged,
      freitext: clampFreitext(merged.freitext, FREITEXT_MAX),
    };
  });

  const setRating = useCallback((id: string, value: number) => {
    setState((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, [id]: value },
    }));
  }, []);

  const setFreitext = useCallback((value: string) => {
    setState((prev) => ({
      ...prev,
      freitext: clampFreitext(value, FREITEXT_MAX),
    }));
  }, []);

  const setReviewerField = useCallback(
    <K extends keyof FormState["reviewer"]>(
      key: K,
      value: FormState["reviewer"][K]
    ) => {
      setState((prev) => ({
        ...prev,
        reviewer: { ...prev.reviewer, [key]: value },
      }));
    },
    []
  );

  const setConsent = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, consent: value }));
  }, []);

  const setDisplayConsent = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, displayConsent: value }));
  }, []);

  const setAnonymous = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, anonymous: value }));
  }, []);

  const submitEnabled = useMemo(() => isSubmitEnabled(state), [state]);

  return {
    state,
    setRating,
    setFreitext,
    setReviewerField,
    setConsent,
    setDisplayConsent,
    setAnonymous,
    submitEnabled,
  };
}
